import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import toast from 'react-hot-toast';

const TOKEN_KEY = 'vldl_token';
const REFRESH_TOKEN_KEY = 'vldl_refresh_token';

class APIService {
  private client: AxiosInstance;
  private baseURL: string;
  private isRefreshing = false;
  private requestQueue: ((token: string) => void)[] = [];

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      // headers: {
      //   'Content-Type': 'application/json',
      // },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      }
    });

    this.setupInterceptors();
  }

  private processQueue(token: string | null): void {
    this.requestQueue.forEach((cb) => {
      if (token) cb(token);
    });
    this.requestQueue = [];
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add session ID for AI monitoring
        const sessionId = sessionStorage.getItem('vldl_session_id') || `session-${Date.now()}`;
        sessionStorage.setItem('vldl_session_id', sessionId);
        config.headers['session-id'] = sessionId;

        // Add user type for AI monitoring
        config.headers['user-type'] = 'vendor';

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor with token refresh logic
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          if (this.isRefreshing) {
            // Queue the request until token refresh completes
            return new Promise((resolve) => {
              this.requestQueue.push((token) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(this.client(originalRequest));
              });
            });
          }

          this.isRefreshing = true;

          try {
            const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

            if (!refreshToken) {
              throw new Error('No refresh token available');
            }

            // Use a fresh axios instance to avoid interceptor loop
            const response = await axios.post(
              `${this.baseURL}/api/v2/auth/refresh`,
              { refreshToken },
              { headers: { 'Content-Type': 'application/json' } }
            );
            const data = response.data?.data || response.data;

            if (!data?.accessToken) {
              throw new Error('Invalid refresh response');
            }

            const newToken = data.accessToken;

            // Update tokens in localStorage
            localStorage.setItem(TOKEN_KEY, newToken);
            if (data.refreshToken) {
              localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
            }

            // Process queued requests with new token
            this.processQueue(newToken);

            // Retry original request with new token
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return this.client(originalRequest);
          } catch (refreshError) {
            // Clear queue and logout on refresh failure
            this.processQueue(null);
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem('vldl_user');

            // Redirect to login
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }

            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        } else if (error.response?.status === 403) {
          toast.error('Access denied. You do not have permission to perform this action.');
        } else if (error.response?.status && error.response.status >= 500) {
          toast.error('Server error. Please try again later.');
        } else if (error.code === 'NETWORK_ERROR') {
          toast.error('Network error. Please check your connection.');
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic request method
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.request<T>(config);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'An error occurred');
    }
  }

  // HTTP Methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  // File upload
  async uploadFile<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }

  // Token refresh
  async refreshToken(): Promise<{ token: string; refreshToken: string }> {
    const refreshToken = localStorage.getItem('vldl_refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    return this.post<{ token: string; refreshToken: string }>('/api/v2/auth/vendor/refresh', {
      refreshToken,
    });
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.get<{ status: string; timestamp: string }>('/health');
  }
}

// Create and export singleton instance
export const apiService = new APIService();

// Specific API endpoint services
export const authAPI = {
  login: (credentials: { email: string; password: string; tenantCode?: string; tenantId?: string }) =>
    apiService.post('/api/v2/auth/vendor/login', credentials),

  lookup: (email: string) =>
    apiService.post('/api/v2/auth/vendor/lookup', { email }),

  logout: () =>
    apiService.post('/api/v2/auth/vendor/logout'),

  refreshToken: () =>
    apiService.post('/api/v2/auth/vendor/refresh'),

  getProfile: () =>
    apiService.get('/api/v2/auth/vendor/me'),

  changePassword: (data: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
    apiService.post('/api/v2/auth/vendor/change-password', data),
};

export const tenantAPI = {
  // Admin tenant management
  getAllTenants: (params?: any) =>
    apiService.get('/api/v2/admin/tenants', { params }),

  getTenant: (id: string) =>
    apiService.get(`/api/v2/vendor/profile/${id}`),

  createTenant: (data: any) =>
    apiService.post('/api/v2/admin/tenants', data),

  updateTenant: (id: string, data: any) =>
    apiService.put(`/api/v2/vendor/profile/${id}`, data),

  deactivateTenant: (id: string) =>
    apiService.delete(`/api/v2/vendor/profile/${id}`),

  // Tenant self-management


  // Analytics
  getAnalytics: (params?: any) =>
    apiService.get('/api/v2/vendor/profile/analytics', { params }),
};


export const orderAPI = {
  getOrders: (params?: any) =>
    apiService.get('/api/v2/vendor/orders', { params }),

  getOrder: (id: string) =>
    apiService.get(`/api/v2/vendor/orders/${id}`),

  updateOrderStatus: (id: string, status: string) =>
    apiService.patch(`/api/v2/vendor/orders/${id}/status`, { status }),

  getOrderStats: (params?: any) =>
    apiService.get('/api/v2/vendor/orders/stats', { params }),

  getRecentOrders: (limit?: number) =>
    apiService.get('/api/v2/vendor/orders/recent', { params: { limit } }),

  // Delivery Status & Tracking
  getDeliveryStatus: (orderId: string) =>
    apiService.get(`/api/v2/vendor/orders/${orderId}/delivery-status`),

  trackOrder: (orderId: string) =>
    apiService.get(`/api/v2/vendor/orders/${orderId}/track`),

  // Vendor Pickup PIN Management
  verifyPickupPin: (orderId: string, pickupPin: string) =>
    apiService.post(`/api/v2/vendor/orders/${orderId}/verify-pickup-pin`, { pickupPin }),

  getPickupPinStatus: (orderId: string) =>
    apiService.get(`/api/v2/vendor/orders/${orderId}/pickup-pin-status`),

  // Order Actions
  cancelOrder: (orderId: string, reason: string) =>
    apiService.post(`/api/v2/vendor/orders/${orderId}/cancel`, { reason }),

  rateOrder: (orderId: string, rating: number, comment?: string) =>
    apiService.post(`/api/v2/vendor/orders/${orderId}/rate`, { rating, comment }),

  // Order Assignment Actions
  offerOrder: (orderId: string, data?: { radius?: number; timeout?: number }) =>
    apiService.post(`/api/v2/vendor/orders/${orderId}/offer`, data || { radius: 5, timeout: 60 }),

  autoAssignOrder: (orderId: string) =>
    apiService.post(`/api/v2/vendor/orders/${orderId}/auto-assign`),

  assignToRider: (orderId: string, riderId: string) =>
    apiService.post(`/api/v2/vendor/orders/${orderId}/assign`, { riderId }),

  getNearbyRiders: (orderId: string, radius?: number) =>
    apiService.get(`/api/v2/vendor/orders/${orderId}/nearby-riders`, { params: { radius: radius || 5 } }),
};

export const riderAPI = {
  // Profile & Status Management
  getProfile: () =>
    apiService.get('/api/v2/rider/profile'),

  updateStatus: (status: string, location?: { latitude: number; longitude: number }) =>
    apiService.post('/api/v2/rider/status', { status, location }),

  updateLocation: (latitude: number, longitude: number) =>
    apiService.post('/api/v2/rider/location', { latitude, longitude }),

  // Order Management
  getNearbyOrders: (latitude: number, longitude: number, radius?: number) =>
    apiService.get('/api/v2/rider/nearby-orders', {
      params: { latitude, longitude, radius }
    }),

  acceptOrder: (orderId: string) =>
    apiService.post(`/api/v2/rider/orders/${orderId}/accept`),

  // Pickup PIN Management (Vendor Verification)
  requestPickupPin: (orderId: string) =>
    apiService.post(`/api/v2/rider/orders/${orderId}/request-pickup-pin`),

  getPickupPin: (orderId: string) =>
    apiService.get(`/api/v2/rider/orders/${orderId}/pickup-pin`),

  // Delivery Management
  startDelivery: (orderId: string, data: {
    currentLocation?: { latitude: number; longitude: number };
    estimatedArrivalTime?: number;
    notes?: string;
  }) =>
    apiService.post(`/api/v2/rider/orders/${orderId}/start-delivery`, data),

  completeDelivery: (orderId: string, data: {
    customerPin: string;
    deliveryLocation?: { latitude: number; longitude: number; address?: string };
    deliveryPhoto?: string;
    customerSignature?: string;
    notes?: string;
  }) =>
    apiService.post(`/api/v2/rider/orders/${orderId}/complete-delivery`, data),

  // Delivery PIN Management (Customer Verification)
  getDeliveryPinStatus: (orderId: string) =>
    apiService.get(`/api/v2/rider/orders/${orderId}/pin-status`),

  regenerateDeliveryPin: (orderId: string) =>
    apiService.post(`/api/v2/rider/orders/${orderId}/regenerate-pin`),

  // Legacy delivery completion (without PIN)
  completeDeliveryLegacy: (orderId: string, data: any) =>
    apiService.post(`/api/v2/rider/orders/${orderId}/complete`, data),

  // Delivery History
  getDeliveries: (params?: { status?: string; limit?: number; offset?: number }) =>
    apiService.get('/api/v2/rider/deliveries', { params }),

  // Emergency Features
  sendSOS: (location: { latitude: number; longitude: number }, message?: string) =>
    apiService.post('/api/v2/rider/sos', { location, message }),

  addEmergencyContact: (contact: { name: string; phone: string; relationship: string }) =>
    apiService.post('/api/v2/rider/emergency-contacts', contact),

  // Incentives & Performance
  getIncentiveStatus: () =>
    apiService.get('/api/v2/rider/incentive-status'),

  getPerformanceMetrics: () =>
    apiService.get('/api/v2/rider/performance-metrics'),

  getAchievements: () =>
    apiService.get('/api/v2/rider/achievements'),

  getLeaderboard: () =>
    apiService.get('/api/v2/rider/leaderboard'),

  getBonusHistory: (params?: any) =>
    apiService.get('/api/v2/rider/bonuses', { params }),

  refreshPerformance: () =>
    apiService.post('/api/v2/rider/refresh-performance'),

  getDailyStats: (date?: string) =>
    apiService.get('/api/v2/rider/daily-stats', { params: { date } }),

  // Admin Functions
  getNearbyRiders: (orderId: string, params?: { radius?: number; limit?: number }) =>
    apiService.get(`/api/v2/rider/nearby/${orderId}`, { params }),
};

export const analyticsAPI = {
  getDashboardMetrics: (period?: string) =>
    apiService.get('/api/v2/vendor/analytics/dashboard', { params: { period } }),

  getRevenueChart: (period?: string) =>
    apiService.get('/api/v2/vendor/analytics/revenue', { params: { period } }),

  getPopularItems: (period?: string) =>
    apiService.get('/api/v2/vendor/analytics/popular-items', { params: { period } }),

  getCustomerInsights: (period?: string) =>
    apiService.get('/api/v2/vendor/analytics/customers', { params: { period } }),

  getCustomerRating: (period?: string) =>
    apiService.get('/api/v2/vendor/analytics/customer-rating', { params: { period } }),

  // Use tenant analytics for comprehensive vendor analytics
  getTenantAnalytics: (params?: any) =>
    apiService.get('/api/v2/vendor/profile/analytics', { params }),

  // Advanced Analytics endpoints (to be created)
  getFinancialMetrics: (period?: string) =>
    apiService.get('/api/v2/vendor/analytics/financial-metrics', { params: { period } }),

  getRevenueBreakdown: (period?: string) =>
    apiService.get('/api/v2/vendor/analytics/revenue-breakdown', { params: { period } }),
};

export const engagementAPI = {
  // Get vendor engagement metrics
  getEngagementMetrics: () =>
    apiService.get('/api/v2/vendor/analytics/engagement'),

  // Get engagement trends over time (if implemented later)
  getEngagementTrends: (params?: { period?: string }) =>
    apiService.get('/api/v2/vendor/analytics/engagement/trends', { params }),

  // Update profile completeness scores
  refreshCompletenessScores: () =>
    apiService.post('/api/v2/vendor/analytics/engagement/refresh'),

  // Mark onboarding as completed
  completeOnboarding: () =>
    apiService.post('/api/v2/vendor/analytics/engagement/onboarding/complete'),
};

export const aiAnalyticsAPI = {
  getDashboard: (params?: any) =>
    apiService.get('/api/v2/vendor/ai-analytics/dashboard', { params }),

  getRealtime: (params?: any) =>
    apiService.get('/api/v2/vendor/ai-analytics/realtime', { params }),

  getEffectiveness: (params?: any) =>
    apiService.get('/api/v2/vendor/ai-analytics/effectiveness', { params }),

  getUserJourney: (userId: string, params?: any) =>
    apiService.get(`/api/v2/vendor/ai-analytics/user-journey/${userId}`, { params }),

  exportMetrics: (params?: any) =>
    apiService.get<Blob>('/api/v2/vendor/ai-analytics/export', { params, responseType: 'blob' }),

  getPredictions: (type: string) =>
    apiService.get(`/api/v2/vendor/ai-analytics/predictions/${type}`),

  getInsights: () =>
    apiService.get('/api/v2/vendor/ai-analytics/insights'),

  getRecommendations: () =>
    apiService.get('/api/v2/vendor/ai-analytics/recommendations'),

  getForecast: (params?: any) =>
    apiService.get('/api/v2/vendor/ai-analytics/forecast', { params }),
};

export const notificationAPI = {
  getNotifications: (params?: any) =>
    apiService.get('/api/v2/vendor/notifications', { params }),

  markAsRead: (id: string) =>
    apiService.patch(`/api/v2/vendor/notifications/${id}/read`),

  markAllAsRead: () =>
    apiService.patch('/api/v2/vendor/notifications/read-all'),

  deleteNotification: (id: string) =>
    apiService.delete(`/api/v2/vendor/notifications/${id}`),
};

export const websiteAPI = {
  // Get website data and analytics for current tenant
  getWebsiteData: () =>
    apiService.get('/api/v2/vendor/website/data/current'),

  getWebsiteStats: () =>
    apiService.get('/api/v2/vendor/website/stats/current'),

  getWebsiteAnalytics: (params?: any) =>
    apiService.get('/api/v2/vendor/website/analytics/current', { params }),

  // Website management
  regenerateWebsite: () =>
    apiService.post('/api/v2/vendor/website/regenerate/current'),

  updateWebsiteSettings: (settings: any) =>
    apiService.put('/api/v2/vendor/website/settings/current', settings),

  updateWebsiteTheme: (theme: string) =>
    apiService.put('/api/v2/vendor/website/theme/current', { websiteTheme: theme }),

  // Website status and management
  getWebsiteStatus: () =>
    apiService.get('/api/v2/vendor/website/status/current'),

  toggleWebsiteStatus: (isActive: boolean) =>
    apiService.patch('/api/v2/vendor/website/status/current', { isActive }),

  // Website preview and testing
  previewWebsite: () =>
    apiService.get('/api/v2/vendor/website/preview/current'),

  generateWebsite: (data: any) =>
    apiService.post('/api/v2/vendor/website/generate', data),

  updateWebsite: (data: any) =>
    apiService.put('/api/v2/vendor/website/update', data),

  getTemplates: () =>
    apiService.get('/api/v2/vendor/website/templates'),

  publishWebsite: () =>
    apiService.post('/api/v2/vendor/website/publish'),

  getMetrics: () =>
    apiService.get('/api/v2/vendor/website/metrics'),
};

export const enhancedGamificationAPI = {
  createTeam: (_body: any) => null,
  joinTeam(_teamId: string, _inviteCode?: string) { },
  leaveTeam(_teamId: string) { },
  joinCompetition(_competitionId: string) { },
  inviteToTeam(_teamId: string, _body: any) { }
};

export const instantPayoutAPI = {
  requestPayout: (amount: number) =>
    apiService.post('/api/v2/vendor/instant-payouts/request', { amount }),

  getPayoutHistory: (params?: any) =>
    apiService.get('/api/v2/vendor/instant-payouts/history', { params }),

  getPayoutStatus: (payoutId: string) =>
    apiService.get(`/api/v2/vendor/instant-payouts/${payoutId}/status`),

  getAvailableBalance: () =>
    apiService.get('/api/v2/vendor/instant-payouts/balance'),

  getPayoutLimits: () =>
    apiService.get('/api/v2/vendor/instant-payouts/limits'),
};

export const walletInterestAPI = {
  // Basic Interest Operations
  getCurrentRate: () =>
    apiService.get('/api/v2/vendor/wallet-interest/rate'),

  getInterestHistory: (params?: any) =>
    apiService.get('/api/v2/vendor/wallet-interest/history', { params }),

  getInterestProjection: (amount: number, days: number) =>
    apiService.get('/api/v2/vendor/wallet-interest/projection', { params: { amount, days } }),

  optIntoInterest: () =>
    apiService.post('/api/v2/vendor/wallet-interest/opt-in'),

  optOutOfInterest: () =>
    apiService.post('/api/v2/vendor/wallet-interest/opt-out'),

  // Advanced Wallet Interest Features
  getConfig: () =>
    apiService.get('/api/v2/vendor/wallet-interest/config'),

  updateConfig: (config: {
    annualInterestRate?: number;
    compoundingFrequency?: 'daily' | 'weekly' | 'monthly';
    minimumBalance?: number;
    maxInterestPerDay?: number;
  }) =>
    apiService.put('/api/v2/vendor/wallet-interest/config', config),

  processInterest: () =>
    apiService.post('/api/v2/vendor/wallet-interest/process'),

  getWalletHistory: (walletId: string, params?: { limit?: number; offset?: number }) =>
    apiService.get(`/api/v2/vendor/wallet-interest/history/${walletId}`, { params }),

  getWalletEarnings: (walletId: string, period?: string) =>
    apiService.get(`/api/v2/vendor/wallet-interest/earnings/${walletId}`, { params: { period } }),

  getProjectionForCurrentUser: (days: number = 30) =>
    apiService.get('/api/v2/vendor/wallet-interest/projection', { params: { days } }),

  getInterestSummary: () =>
    apiService.get('/api/v2/vendor/wallet-interest/summary'),
};

// Payout Monitoring API (Missing from frontend)
export const payoutMonitoringAPI = {
  getMetrics: (timeframe: string = '1 hour') =>
    apiService.get('/api/v2/vendor/monitoring/payout/metrics', { params: { timeframe } }),

  getHealthStatus: () =>
    apiService.get('/api/v2/vendor/monitoring/payout/health'),

  getFailures: (params?: any) =>
    apiService.get('/api/v2/vendor/monitoring/payout/failures', { params }),

  resolveFailure: (id: string, resolution: string) =>
    apiService.post(`/api/v2/vendor/monitoring/payout/failures/${id}/resolve`, { resolution }),

  retryFailure: (id: string) =>
    apiService.post(`/api/v2/vendor/monitoring/payout/failures/${id}/retry`),

  getOperationsTickets: (params?: any) =>
    apiService.get('/api/v2/vendor/monitoring/payout/operations-tickets', { params }),

  updateOperationsTicket: (id: string, update: any) =>
    apiService.put(`/api/v2/vendor/monitoring/payout/operations-tickets/${id}`, update),

  getDashboard: () =>
    apiService.get('/api/v2/vendor/monitoring/payout/dashboard'),

  testAlert: (data: any) =>
    apiService.post('/api/v2/vendor/monitoring/payout/test-alert', data),
};

// Enhanced Personalization API
export const enhancedPersonalizationAPI = {
  getRecommendations: () =>
    apiService.get('/api/v2/vendor/personalization/recommendations'),

  getSmartSuggestions: () =>
    apiService.get('/api/v2/vendor/personalization/smart-suggestions'),

  learnFromOrder: (orderData: any) =>
    apiService.post('/api/v2/vendor/personalization/learn-order', orderData),

  getActivationStatus: () =>
    apiService.get('/api/v2/vendor/personalization/activation-status'),
};

// Enhanced Rider Incentives API
export const enhancedRiderIncentivesAPI = {
  getIncentiveStatus: () =>
    apiService.get('/api/v2/rider/incentives/incentive-status'),

  getPerformanceMetrics: () =>
    apiService.get('/api/v2/rider/incentives/performance-metrics'),

  getAchievements: () =>
    apiService.get('/api/v2/rider/incentives/achievements'),

  getLeaderboard: () =>
    apiService.get('/api/v2/rider/incentives/leaderboard'),

  getBonuses: () =>
    apiService.get('/api/v2/rider/incentives/bonuses'),

  refreshPerformance: () =>
    apiService.post('/api/v2/rider/incentives/refresh-performance'),

  getDailyStats: (date?: string) =>
    apiService.get('/api/v2/rider/incentives/daily-stats', { params: { date } }),

  // Admin functions
  getPerformanceOverview: () =>
    apiService.get('/api/v2/rider/incentives/admin/performance-overview'),

  refreshLeaderboard: () =>
    apiService.post('/api/v2/rider/incentives/admin/refresh-leaderboard'),
};

// Enhanced WhatsApp API
export const enhancedWhatsappAPI = {
  // Webhook management
  getWebhookVerification: (hub_challenge?: string) =>
    apiService.get('/api/v2/vendor/whatsapp/webhook', { params: { 'hub.challenge': hub_challenge } }),

  // Message management
  sendMessage: (data: { to: string; message: string; type?: string }) =>
    apiService.post('/api/v2/vendor/whatsapp/send', data),

  sendTemplate: (data: any) =>
    apiService.post('/api/v2/vendor/whatsapp/template', data),

  getMessages: (phoneNumber: string, params?: any) =>
    apiService.get(`/api/v2/vendor/whatsapp/messages/${phoneNumber}`, { params }),

  getConversations: (params?: any) =>
    apiService.get('/api/v2/vendor/whatsapp/conversations', { params }),

  updateConversationStatus: (phoneNumber: string, status: string) =>
    apiService.patch(`/api/v2/vendor/whatsapp/conversations/${phoneNumber}/status`, { status }),

  // Profile management
  getProfile: () =>
    apiService.get('/api/v2/vendor/whatsapp/profile'),

  updateProfile: (data: any) =>
    apiService.put('/api/v2/vendor/whatsapp/profile', data),

  // Templates and analytics
  getTemplates: () =>
    apiService.get('/api/v2/vendor/whatsapp/templates'),

  getAnalytics: (params?: any) =>
    apiService.get('/api/v2/vendor/whatsapp/analytics', { params }),

  testWebhook: () =>
    apiService.post('/api/v2/vendor/whatsapp/test'),

  // Takeover functionality
  requestTakeover: (data: { reason: string; priority?: string }) =>
    apiService.post('/api/v2/vendor/whatsapp-takeover/request', data),

  getTakeoverStatus: () =>
    apiService.get('/api/v2/vendor/whatsapp-takeover/status'),

  approveTakeover: (takeoverId: string) =>
    apiService.post(`/api/v2/vendor/whatsapp-takeover/${takeoverId}/approve`),

  endTakeover: (takeoverId: string) =>
    apiService.post(`/api/v2/vendor/whatsapp-takeover/${takeoverId}/end`),

  getTakeoverHistory: (params?: any) =>
    apiService.get('/api/v2/vendor/whatsapp-takeover/history', { params }),
};

// Advanced Payment Management API
export const advancedPaymentAPI = {
  // Payment initialization and verification
  initializePayment: (data: any) =>
    apiService.post('/api/v2/customer/payments/initialize', data),

  verifyPayment: (reference: string) =>
    apiService.post('/api/v2/customer/payments/verify', { reference }),

  getPaymentStatus: (reference: string) =>
    apiService.get(`/api/v2/vendor/payments/${reference}`),

  getPaymentHistory: (params?: any) =>
    apiService.get('/api/v2/vendor/payments/history', { params }),

  getPaymentStats: (params?: any) =>
    apiService.get('/api/v2/vendor/payments/stats', { params }),

  // Refund management
  processRefund: (orderId: string, data: { reason: string; amount?: number }) =>
    apiService.post(`/api/v2/vendor/payments/${orderId}/refund`, data),

  getRefunds: (params?: any) =>
    apiService.get('/api/v2/vendor/payments/refunds/list', { params }),

  // Payout management
  createPayout: (data: any) =>
    apiService.post('/api/v2/vendor/enhanced-payouts/request', data),

  getPayouts: (params?: any) =>
    apiService.get('/api/v2/vendor/enhanced-payouts/history', { params }),
};

// Enhanced Gamification API with Team Features
export const gamificationAPI = {
  // Profile and badges
  getProfile: () =>
    apiService.get('/api/v2/vendor/gamification/profile'),

  getBadges: () =>
    apiService.get('/api/v2/vendor/gamification/badges'),

  showcaseBadge: (badgeId: string) =>
    apiService.post(`/api/v2/vendor/gamification/badges/${badgeId}/showcase`),

  // Challenges
  getChallenges: () =>
    apiService.get('/api/v2/vendor/gamification/challenges'),

  joinChallenge: (challengeId: string) =>
    apiService.post(`/api/v2/vendor/gamification/challenges/${challengeId}/join`),

  getChallengeProgress: (challengeId: string) =>
    apiService.get(`/api/v2/vendor/gamification/challenges/${challengeId}/progress`),

  // Leaderboards
  getLeaderboard: (type: string, params?: any) =>
    apiService.get(`/api/v2/vendor/gamification/leaderboard/${type}`, { params }),

  // Team features
  getTeams: () =>
    apiService.get('/api/v2/vendor/gamification/teams'),

  createTeam: (data: { name: string; description?: string }) =>
    apiService.post('/api/v2/vendor/gamification/teams', data),

  joinTeam: (teamId: string, inviteCode?: string) =>
    apiService.post(`/api/v2/vendor/gamification/teams/${teamId}/join`, { inviteCode }),

  leaveTeam: (teamId: string) =>
    apiService.post(`/api/v2/vendor/gamification/teams/${teamId}/leave`),

  getTeamStats: (teamId: string) =>
    apiService.get(`/api/v2/vendor/gamification/teams/${teamId}/stats`),

  inviteToTeam: (teamId: string, data: { email?: string; phone?: string; riderId?: string }) =>
    apiService.post(`/api/v2/vendor/gamification/teams/${teamId}/invite`, data),

  // Competitions
  getCompetitions: (params?: any) =>
    apiService.get('/api/v2/vendor/gamification/competitions', { params }),

  joinCompetition: (competitionId: string) =>
    apiService.post(`/api/v2/vendor/gamification/competitions/${competitionId}/join`),

  getCompetitionLeaderboard: (competitionId: string) =>
    apiService.get(`/api/v2/vendor/gamification/competitions/${competitionId}/leaderboard`),
};

// Add inventory API
export const inventoryAPI = {
  getItems: (params?: any) =>
    apiService.get('/api/v2/vendor/inventory', { params }),

  getItem: (id: string) =>
    apiService.get(`/api/v2/vendor/inventory/items/${id}`),

  getStatistics: () =>
    apiService.get('/api/v2/vendor/inventory/statistics'),

  createItem: (data: any) =>
    apiService.post('/api/v2/vendor/inventory/items', data),

  updateItem: (id: string, data: any) =>
    apiService.put(`/api/v2/vendor/inventory/items/${id}`, data),

  deleteItem: (id: string) =>
    apiService.delete(`/api/v2/vendor/inventory/items/${id}`),

  updateStock: (id: string, quantity: number) =>
    apiService.post(`/api/v2/vendor/inventory/${id}/stock`, {
      quantity,
      transactionType: 'adjustment',
      reason: 'Manual stock adjustment'
    }),

  getStockAlerts: () =>
    apiService.get('/api/v2/vendor/inventory/alerts'),

  bulkUpdate: (updates: any[]) =>
    apiService.post('/api/v2/vendor/inventory/bulk-update', { updates }),

  // Inventory Analytics Endpoints
  getCostOptimization: (params?: { period?: string }) =>
    apiService.get('/api/v2/vendor/inventory/analytics/cost-optimization', { params }),

  getSupplierPerformance: (params?: { period?: string }) =>
    apiService.get('/api/v2/vendor/inventory/analytics/supplier-performance', { params }),

  getInventoryTurnover: (params?: { period?: string }) =>
    apiService.get('/api/v2/vendor/inventory/analytics/turnover', { params }),

  getCostAnalysis: (params?: { period?: string }) =>
    apiService.get('/api/v2/vendor/inventory/analytics/cost-analysis', { params }),

  // AI Predictions Endpoint
  getAIPredictions: (params?: { timeframe?: string }) =>
    apiService.get('/api/v2/inventory/ai-predictions', { params }),
};

// WhatsApp Business Management API
export const whatsappAPI = {
  // Conversation management
  getConversations: () =>
    apiService.get('/api/v2/vendor/whatsapp-takeover/conversations'),

  toggleMode: (data: { customerPhone: string; mode: string; reason?: string }) =>
    apiService.post('/api/v2/vendor/whatsapp-takeover/toggle-mode', data),

  sendMessage: (data: { customerPhone: string; message: string; type?: string }) =>
    apiService.post('/api/v2/vendor/whatsapp-takeover/send-message', data),

  getConversationHistory: (customerPhone: string) =>
    apiService.get(`/api/v2/vendor/whatsapp-takeover/history/${customerPhone}`),

  getActiveChats: () =>
    apiService.get('/api/v2/vendor/whatsapp-takeover/active-chats'),

  getMetrics: () =>
    apiService.get('/api/v2/vendor/whatsapp-takeover/metrics'),
};

// Gamification API for vendors
export const vendorGamificationAPI = {
  getProfile: () =>
    apiService.get('/api/v2/vendor/gamification/profile'),

  getBadges: () =>
    apiService.get('/api/v2/vendor/gamification/badges'),

  getChallenges: () =>
    apiService.get('/api/v2/vendor/gamification/challenges'),

  joinChallenge: (challengeId: string) =>
    apiService.post(`/api/v2/vendor/gamification/challenges/${challengeId}/join`),

  getLeaderboard: (type: string = 'weekly') =>
    apiService.get(`/api/v2/vendor/gamification/leaderboard/${type}`),

  getRewards: () =>
    apiService.get('/api/v2/vendor/gamification/rewards'),

  claimReward: (rewardId: string) =>
    apiService.post(`/api/v2/vendor/gamification/rewards/${rewardId}/claim`),

  getProgress: () =>
    apiService.get('/api/v2/vendor/gamification/progress'),
};

// Wallet Management API
export const walletAPI = {
  getBalance: () =>
    apiService.get('/api/v2/vendor/wallets/balance'),

  getTransactions: (params?: any) =>
    apiService.get('/api/v2/vendor/wallets/transactions', { params }),

  getInterestRates: () =>
    apiService.get('/api/v2/vendor/wallet-interest/rates'),

  getInterestHistory: () =>
    apiService.get('/api/v2/vendor/wallet-interest/history'),

  requestPayout: (data: { amount: number; method: string }) =>
    apiService.post('/api/v2/vendor/enhanced-payouts/request', data),

  getPayoutHistory: () =>
    apiService.get('/api/v2/vendor/enhanced-payouts/history'),

  getPayoutMethods: () =>
    apiService.get('/api/v2/vendor/enhanced-payouts/methods'),
};

// Personalization API
export const personalizationAPI = {
  getCustomerInsights: () =>
    apiService.get('/api/v2/vendor/personalization/insights'),

  getRecommendations: (customerId: string) =>
    apiService.get(`/api/v2/vendor/personalization/recommendations/${customerId}`),

  updatePreferences: (data: any) =>
    apiService.put('/api/v2/vendor/personalization/preferences', data),

  getSegments: () =>
    apiService.get('/api/v2/vendor/personalization/segments'),

  getPerformanceMetrics: () =>
    apiService.get('/api/v2/vendor/personalization/metrics'),
};

// Commission Management API
export const commissionAPI = {
  getCurrentRates: () =>
    apiService.get('/api/v2/vendor/commission/rates'),

  getHistory: () =>
    apiService.get('/api/v2/vendor/commission/history'),

  getProjections: () =>
    apiService.get('/api/v2/vendor/commission/projections'),

  negotiateRate: (data: any) =>
    apiService.post('/api/v2/vendor/commission/negotiate', data),
};

// AI Feedback API
export const aiFeedbackAPI = {
  submitFeedback: (data: any) =>
    apiService.post('/api/v2/ai/feedback', data),

  getRecommendations: () =>
    apiService.get('/api/v2/ai/recommendations'),

  getInsights: () =>
    apiService.get('/api/v2/ai/insights'),
};

// Vendor-specific API using v2 and direct endpoints
export const vendorAPI = {
  // Customer Analytics
  getCustomerAnalytics: () =>
    apiService.get('/api/v2/vendor/customers/analytics'),

  // Direct Wallet Endpoints (vendor-optimized)
  getCurrentWallet: () =>
    apiService.get('/api/v2/vendor/wallets-direct/current'),

  getWalletTransactions: (params?: { limit?: number; offset?: number }) =>
    apiService.get('/api/v2/vendor/wallets-direct/transactions', { params }),

  getWalletSummary: () =>
    apiService.get('/api/v2/vendor/wallets-direct/summary'),

  // Direct Payout Endpoints (vendor-optimized)
  getBankAccounts: () =>
    apiService.get('/api/v2/vendor/payouts-direct/bank-accounts'),

  getPayoutHistory: (params?: { limit?: number; offset?: number }) =>
    apiService.get('/api/v2/vendor/payouts-direct/history', { params }),

  getPayoutDashboard: () =>
    apiService.get('/api/v2/vendor/payouts-direct/dashboard'),

  // Direct WhatsApp Endpoints (vendor-optimized)
  getWhatsAppConversations: () =>
    apiService.get('/api/v2/vendor/whatsapp-direct/conversations'),

  getWhatsAppAnalytics: () =>
    apiService.get('/api/v2/vendor/whatsapp-direct/analytics'),
};

// Customer Analytics API
export const customerAPI = {
  getAnalytics: (params?: { period?: string }) =>
    apiService.get('/api/v2/vendor/customers/analytics', { params }),

  getSegments: () =>
    apiService.get('/api/v2/vendor/customers/segments'),

  getChurnAnalysis: () =>
    apiService.get('/api/v2/vendor/customers/churn-analysis'),

  getSatisfaction: () =>
    apiService.get('/api/v2/vendor/customers/satisfaction'),

  getDemographics: () =>
    apiService.get('/api/v2/vendor/customers/demographics'),
};

export default apiService;